export interface OrgCardDetailRowInput {
  id: string;
  label: string;
  value: string;
}

export interface OrgCardDetailRowLayout extends OrgCardDetailRowInput {
  y: number;
  height: number;
}

interface LayoutOptions {
  rows: OrgCardDetailRowInput[];
  valueWidth: number;
  fontSize: number;
  lineHeightPx: number;
  availableHeightPx: number;
  maxLinesPerRow?: number;
  rowGapPx?: number;
}

interface LayoutResult {
  rows: OrgCardDetailRowLayout[];
  hiddenRows: number;
}

let measurementCanvas: HTMLCanvasElement | null = null;

function getMeasurementContext(fontSize: number): CanvasRenderingContext2D | null {
  if (typeof document === 'undefined') return null;
  if (!measurementCanvas) {
    measurementCanvas = document.createElement('canvas');
  }
  const ctx = measurementCanvas.getContext('2d');
  if (!ctx) return null;
  ctx.font = `${fontSize}px Inter, sans-serif`;
  return ctx;
}

function estimateWrappedLines(text: string, maxWidth: number, fontSize: number): number {
  const value = text.trim();
  if (!value) return 1;
  if (!Number.isFinite(maxWidth) || maxWidth <= 0) return 1;

  const ctx = getMeasurementContext(fontSize);
  if (!ctx) return 1;

  const words = value.split(/\s+/);
  let lines = 1;
  let current = '';

  for (const word of words) {
    const trial = current ? `${current} ${word}` : word;
    if (ctx.measureText(trial).width <= maxWidth) {
      current = trial;
      continue;
    }
    if (!current) {
      let chunk = '';
      for (const char of word) {
        const next = chunk + char;
        if (ctx.measureText(next).width <= maxWidth) {
          chunk = next;
          continue;
        }
        lines += 1;
        chunk = char;
      }
      current = chunk;
      continue;
    }
    lines += 1;
    current = word;
  }

  return Math.max(1, lines);
}

export function layoutOrgCardDetailRows(options: LayoutOptions): LayoutResult {
  const {
    rows,
    valueWidth,
    fontSize,
    lineHeightPx,
    availableHeightPx,
    maxLinesPerRow = 3,
    rowGapPx = 4,
  } = options;

  const visibleRows: OrgCardDetailRowLayout[] = [];
  let y = 0;

  for (let i = 0; i < rows.length; i += 1) {
    const row = rows[i];
    const remaining = availableHeightPx - y;
    if (remaining < lineHeightPx) {
      return { rows: visibleRows, hiddenRows: rows.length - i };
    }

    const estimatedLines = estimateWrappedLines(row.value, valueWidth, fontSize);
    const clampedLines = Math.max(1, Math.min(maxLinesPerRow, estimatedLines));
    const idealHeight = clampedLines * lineHeightPx;
    const rowHeight = Math.max(lineHeightPx, Math.min(idealHeight, remaining));

    visibleRows.push({
      ...row,
      y,
      height: rowHeight,
    });

    if (i < rows.length - 1) {
      y += rowHeight + rowGapPx;
    } else {
      y += rowHeight;
    }
  }

  return { rows: visibleRows, hiddenRows: 0 };
}
